<template>
   <LoadingPage v-if="isLoading"></LoadingPage>
   <div class="leads-page leads-page-v2 p-3">
      <!-- ── Page Header ── -->
      <div class="ms-page-header">
         <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
            <div class="d-flex align-items-center gap-3">
               <div class="ms-page-header__icon">
                  <i class="fa-solid fa-users"></i>
               </div>
               <div>
                  <p class="ms-page-header__breadcrumb">Sales Pipeline <span class="ms-breadcrumb-sep">/</span> Leads</p>
                  <div class="d-flex align-items-center flex-wrap gap-2 mt-1">
                     <h1 class="ms-page-header__title">Leads Workspace</h1>
                     <span class="badge bg-primary-soft text-primary rounded-pill px-2 py-1 fs-xs fw-bold">
                        {{ pagination.totalItems }} Total
                     </span>
                  </div>
               </div>
            </div>

            <!-- Header Action CTAs -->
            <div class="d-flex align-items-center gap-2 flex-wrap">
               <button v-if="$store.getters['auth/canExportLeads']" type="button" class="action-btn-circle action-btn-circle--primary"
                  @click="exportLeadsToCSV()" :disabled="isLoading" title="Export leads list to CSV" aria-label="Export CSV">
                  <i class="fa-solid fa-file-csv"></i>
               </button>

               <button type="button" class="action-btn-circle action-btn-circle--primary"
                  @click="showFilterSettingsModal = true" title="Configure filters & exclusions" aria-label="Filter Settings">
                  <i class="fa-solid fa-sliders"></i>
               </button>

               <button type="button" class="action-btn-circle action-btn-circle--primary"
                  data-bs-toggle="modal" data-bs-target="#leadForm" @click="onButtonClick('create', '')" title="Create Lead" aria-label="Create Lead">
                  <i class="fa-solid fa-plus"></i>
               </button>
            </div>
         </div>
      </div>
      <!-- ── Main Workspace Card ── -->
      <div class="card modern-table-card border-0 shadow-sm">
         <div class="card-body p-3">

            <!-- ── Quick Tabs & Segmented Filters ── -->
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 border-bottom mb-3">
               <div class="modern-tabs">
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'all' }" @click="setQuickTab('all')">
                     <i class="fa-solid fa-list me-1"></i> All Leads
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'unassigned' }" @click="setQuickTab('unassigned')">
                     <i class="fa-solid fa-triangle-exclamation me-1 text-warning"></i> Needs BDM
                     <span v-if="unassignedCount > 0" class="badge bg-amber-soft text-amber rounded-pill ms-1 fs-xs">{{ unassignedCount }}</span>
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'proposals' }" @click="setQuickTab('proposals')">
                     <i class="fa-solid fa-paper-plane me-1"></i> Proposals In Flight
                     <span v-if="proposalInFlightCount > 0" class="badge bg-teal-soft text-teal rounded-pill ms-1 fs-xs">{{ proposalInFlightCount }}</span>
                  </button>
                  <button type="button" class="modern-tab" :class="{ active: activeQuickTab === 'high_priority' }" @click="setQuickTab('high_priority')">
                     <i class="fa-solid fa-fire me-1 text-danger"></i> High Priority
                     <span v-if="highPriorityCount > 0" class="badge bg-rose-soft text-rose rounded-pill ms-1 fs-xs">{{ highPriorityCount }}</span>
                  </button>
               </div>

               <!-- Pagination & View Controls -->
               <div class="d-flex align-items-center gap-2 ms-auto">
                  <div class="d-flex align-items-center gap-1">
                     <span class="text-muted fs-sm">Rows:</span>
                     <select v-model="pagination.pageSize" class="form-select form-select-sm modern-select" style="width: 70px;">
                        <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                     </select>
                  </div>

                  <div class="pagination-modern">
                     <button @click="previousPage()" :disabled="pagination.page === 1" class="pagination-arrow" title="Previous Page">
                        <i class="fa-solid fa-chevron-left"></i>
                     </button>
                     <span class="pagination-label">
                        <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                     </span>
                     <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages || pagination.totalItems === 0" class="pagination-arrow" title="Next Page">
                        <i class="fa-solid fa-chevron-right"></i>
                     </button>
                  </div>
               </div>
            </div>

            <!-- ── Search & Filter Omnibar ── -->
            <div class="row g-2 align-items-center mb-3">
               <!-- Search Input -->
               <div class="col-12 col-md-4 col-lg-4">
                  <div class="modern-search-wrapper">
                     <i class="fa-solid fa-magnifying-glass search-icon"></i>
                     <input v-model.trim="search" type="text" class="form-control form-control-sm modern-search-input"
                        placeholder="Search contact, organisation, email, phone..."
                        @keyup="onSearch()" />
                     <button v-if="search" type="button" class="btn-clear-search" @click="clearSearch" title="Clear search">
                        <i class="fa-solid fa-xmark"></i>
                     </button>
                  </div>
               </div>

               <!-- Filter Dropdowns -->
               <div class="col-12 col-md-8 col-lg-8 d-flex flex-wrap align-items-center gap-2 justify-content-md-start justify-content-lg-start">
                  <!-- Status Filter Dropdown -->
                  <div class="dropdown">
                     <button type="button" class="btn modern-filter-btn btn-sm" data-bs-toggle="dropdown" aria-expanded="false">
                        <span>Status</span>
                        <span v-if="statusFilter.length > 0" class="badge rounded-pill bg-teal text-white ms-1">{{ statusFilter.length }}</span>
                        <i class="fa-solid fa-chevron-down ms-1 fs-xs text-muted"></i>
                     </button>
                     <div class="dropdown-menu p-2 shadow-lg border-0 modern-dropdown-menu" @click.stop>
                        <div class="dropdown-header px-2 py-1 text-uppercase fs-xs fw-bold text-muted">Filter by Status</div>
                        <div class="d-flex flex-column gap-1 overflow-auto" style="max-height: 240px;">
                           <div v-for="opt in leadStatusTypes" :key="opt.id" class="dropdown-item-checkbox">
                              <label class="d-flex align-items-center cursor-pointer m-0 w-100 p-1">
                                 <input class="form-check-input m-0 me-2" type="checkbox" :value="opt.id" v-model="statusFilter">
                                 <span class="fs-sm">{{ opt.display_name }}</span>
                              </label>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Priority Filter Dropdown -->
                  <div class="dropdown">
                     <button type="button" class="btn modern-filter-btn btn-sm" data-bs-toggle="dropdown" aria-expanded="false">
                        <span>Priority</span>
                        <span v-if="priorityFilter.length > 0" class="badge rounded-pill bg-teal text-white ms-1">{{ priorityFilter.length }}</span>
                        <i class="fa-solid fa-chevron-down ms-1 fs-xs text-muted"></i>
                     </button>
                     <div class="dropdown-menu p-2 shadow-lg border-0 modern-dropdown-menu" @click.stop>
                        <div class="dropdown-header px-2 py-1 text-uppercase fs-xs fw-bold text-muted">Filter by Priority</div>
                        <div class="d-flex flex-column gap-1 overflow-auto" style="max-height: 240px;">
                           <div v-for="opt in leadPriorityTypes" :key="opt.id" class="dropdown-item-checkbox">
                              <label class="d-flex align-items-center cursor-pointer m-0 w-100 p-1">
                                 <input class="form-check-input m-0 me-2" type="checkbox" :value="opt.id" v-model="priorityFilter">
                                 <span class="fs-sm">{{ opt.display_name }}</span>
                              </label>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Lead Type Filter Dropdown -->
                  <div class="dropdown">
                     <button type="button" class="btn modern-filter-btn btn-sm" data-bs-toggle="dropdown" aria-expanded="false">
                        <span>Lead Type</span>
                        <span v-if="typeFilter.length > 0" class="badge rounded-pill bg-teal text-white ms-1">{{ typeFilter.length }}</span>
                        <i class="fa-solid fa-chevron-down ms-1 fs-xs text-muted"></i>
                     </button>
                     <div class="dropdown-menu p-2 shadow-lg border-0 modern-dropdown-menu" @click.stop>
                        <div class="dropdown-header px-2 py-1 text-uppercase fs-xs fw-bold text-muted">Filter by Lead Type</div>
                        <div class="d-flex flex-column gap-1 overflow-auto" style="max-height: 240px;">
                           <div v-for="opt in leadTypeList" :key="opt.id" class="dropdown-item-checkbox">
                              <label class="d-flex align-items-center cursor-pointer m-0 w-100 p-1">
                                 <input class="form-check-input m-0 me-2" type="checkbox" :value="opt.id" v-model="typeFilter">
                                 <span class="fs-sm">{{ opt.display_name }}</span>
                              </label>
                           </div>
                        </div>
                     </div>
                  </div>

                  <!-- Active Filters Chips Bar -->
                  <div v-if="isFiltered" class="d-flex flex-wrap align-items-center gap-1 ms-1">
                     <span v-for="val in statusFilter" :key="'status-' + val" class="filter-chip">
                        <span>Status: {{ getStatusDisplayName(val) }}</span>
                        <i class="fa-solid fa-xmark cursor-pointer" @click="removeStatusFilter(val)"></i>
                     </span>
                     <span v-for="val in priorityFilter" :key="'priority-' + val" class="filter-chip">
                        <span>Priority: {{ getPriorityDisplayName(val) }}</span>
                        <i class="fa-solid fa-xmark cursor-pointer" @click="removePriorityFilter(val)"></i>
                     </span>
                     <span v-for="val in typeFilter" :key="'type-' + val" class="filter-chip">
                        <span>Type: {{ getTypeDisplayName(val) }}</span>
                        <i class="fa-solid fa-xmark cursor-pointer" @click="removeTypeFilter(val)"></i>
                     </span>
                     <button type="button" class="btn btn-link btn-sm text-decoration-none p-0 ms-1 text-teal fs-xs fw-bold" @click="clearAllFiltersAndSearch">
                        Clear All
                     </button>
                  </div>
               </div>
            </div>

            <!-- ── Leads Modern Data Grid ── -->
            <div class="table-responsive modern-table-wrapper">
               <table class="table modern-table align-middle mb-0">
                  <thead>
                     <tr>
                        <th scope="col" style="min-width: 260px;" class="sortable-th" :class="{ 'sorted-th': isSorted('organisation_name') }" @click="toggleSort('organisation_name', 'asc')" title="Sort by Contact & Organisation">
                           <div class="d-flex align-items-center gap-1">
                              <span>Contact & Organisation</span>
                              <i :class="getSortIcon('organisation_name')"></i>
                           </div>
                        </th>
                        <th scope="col" style="min-width: 150px;" class="sortable-th" :class="{ 'sorted-th': isSorted('type') }" @click="toggleSort('type', 'asc')" title="Sort by Type">
                           <div class="d-flex align-items-center gap-1">
                              <span>Type</span>
                              <i :class="getSortIcon('type')"></i>
                           </div>
                        </th>
                        <th scope="col" style="min-width: 140px; text-align: center;" class="sortable-th" :class="{ 'sorted-th': isSorted('priority') }" @click="toggleSort('priority', 'asc')" title="Sort by Priority">
                           <div class="d-flex align-items-center justify-content-center gap-1">
                              <span>Priority</span>
                              <i :class="getSortIcon('priority')"></i>
                           </div>
                        </th>
                        <th scope="col" style="min-width: 160px; text-align: center;" class="sortable-th" :class="{ 'sorted-th': isSorted('status') }" @click="toggleSort('status', 'asc')" title="Sort by Status">
                           <div class="d-flex align-items-center justify-content-center gap-1">
                              <span>Status</span>
                              <i :class="getSortIcon('status')"></i>
                           </div>
                        </th>
                        <th scope="col" style="min-width: 160px;" class="sortable-th" :class="{ 'sorted-th': isSorted('assigned_user_id') }" @click="toggleSort('assigned_user_id', 'asc')" title="Sort by Assigned BDM">
                           <div class="d-flex align-items-center gap-1">
                              <span>Assigned BDM</span>
                              <i :class="getSortIcon('assigned_user_id')"></i>
                           </div>
                        </th>
                        <th scope="col" style="min-width: 120px;" class="sortable-th" :class="{ 'sorted-th': isSorted('created_at') }" @click="toggleSort('created_at', 'desc')" title="Sort by Created Date">
                           <div class="d-flex align-items-center gap-1">
                              <span>Created</span>
                              <i :class="getSortIcon('created_at')"></i>
                           </div>
                        </th>
                        <th scope="col" style="min-width: 140px; text-align: right;">Action</th>
                     </tr>
                  </thead>

                  <TableEmptyState
                     v-if="pagination.totalItems <= 0"
                     :colspan="7"
                     icon="fa-solid fa-users"
                     title="No Leads Found"
                     description="Your leads pipeline is currently empty. Create your first lead to start tracking potential clients and proposals."
                     action-text="Create Lead"
                     action-icon="fa-solid fa-plus"
                     :is-filtered="isFiltered"
                     filtered-title="No Matching Leads Found"
                     filtered-description="No leads match your current search query or active filter criteria. Try adjusting or clearing your filters."
                     clear-filters-text="Reset Filters & Search"
                     @action="openCreateLeadModal"
                     @clear-filters="clearAllFiltersAndSearch"
                  />

                  <tbody v-else>
                     <tr v-for="lead in leads" :key="lead.id" class="modern-row">
                        <!-- Contact & Organisation Cell -->
                        <td @click="onButtonClick('view', lead.id)" data-bs-toggle="offcanvas" data-bs-target="#leadSidebar" aria-controls="leadSidebar" class="cursor-pointer">
                           <div class="d-flex align-items-center gap-3">
                              <!-- Avatar / Initials -->
                              <div class="lead-avatar" :style="{ backgroundColor: getLeadAvatarColor(lead) }">
                                 {{ getLeadInitials(lead) }}
                              </div>

                              <div class="d-flex flex-column">
                                 <!-- Contact Name -->
                                 <div class="d-flex align-items-center gap-2 flex-wrap">
                                    <span class="lead-primary-name">
                                       {{ getLeadDisplayName(lead) }}
                                    </span>
                                    <span v-if="lead.is_test_data" class="org-pill-badge org-pill-badge--test">
                                       Test
                                    </span>
                                 </div>

                                 <!-- Organisation & Synced Badge -->
                                 <div class="d-flex align-items-center gap-1 flex-wrap mt-1">
                                    <span class="text-secondary fs-sm fw-500">
                                       <i class="fa-solid fa-building me-1 text-muted fs-xs"></i>
                                       {{ lead.organisation_name || 'No organisation' }}
                                    </span>

                                    <!-- Auth Synced -->
                                    <span v-if="(lead.organisation && (lead.organisation.is_auth_synced || lead.organisation.has_auth_organisation == 1)) || lead.is_auth_synced || lead.has_auth_organisation == 1"
                                       class="org-pill-badge org-pill-badge--synced"
                                       :title="'Synced with Auth' + ((lead.organisation && lead.organisation.auth_organisation_name) ? ': ' + lead.organisation.auth_organisation_name : (lead.auth_organisation_name ? ': ' + lead.auth_organisation_name : ''))">
                                       <i class="fas fa-sync-alt"></i> Synced
                                    </span>
                                    <!-- Marked for Creation -->
                                    <span v-else-if="(lead.organisation && lead.organisation.has_auth_organisation == 2) || lead.has_auth_organisation == 2"
                                       class="org-pill-badge org-pill-badge--marked" title="Marked for creation in Auth">
                                       <i class="fas fa-clock"></i>
                                    </span>
                                 </div>

                                 <!-- Contact micro-details (email / phone) -->
                                 <div v-if="lead.email || lead.phone" class="d-flex align-items-center gap-2 mt-1 text-muted fs-xs">
                                    <span v-if="lead.email" class="text-truncate" style="max-width: 200px;" :title="lead.email">
                                       <i class="fa-regular fa-envelope me-1"></i>{{ lead.email }}
                                    </span>
                                    <span v-if="lead.phone" class="text-nowrap">
                                       <i class="fa-solid fa-phone me-1"></i>{{ lead.phone }}
                                    </span>
                                 </div>

                                 <!-- Former Org Name -->
                                 <small v-if="getFormerLeadOrgName(lead)" class="text-muted fst-italic mt-1" style="font-size: 0.72rem;">
                                    <i class="fa-solid fa-history me-1"></i> Formerly: {{ getFormerLeadOrgName(lead) }}
                                 </small>
                              </div>
                           </div>
                        </td>

                        <!-- Lead Type Cell -->
                        <td @click="onButtonClick('view', lead.id)" data-bs-toggle="offcanvas" data-bs-target="#leadSidebar" aria-controls="leadSidebar" class="cursor-pointer">
                           <span class="lead-type-badge">
                              <i class="fa-regular fa-folder-open me-1"></i>
                              {{ lead.lead_type_id || '-' }}
                           </span>
                        </td>

                        <!-- Priority Cell -->
                        <td style="position: relative; text-align: center;">
                           <span class="spark-pill-badge" :class="[getLeadPriorityBadgeClass(lead.lead_priority_id), { 'not-allowed-cursor': isLeadStatusSpam(lead) }]">
                              <i :class="getLeadPriorityIcon(lead.lead_priority_id)"></i>
                              {{ getPriorityName(lead.lead_priority_id) }}
                           </span>

                           <select v-if="!isLeadStatusSpam(lead)" class="form-select table-select-priority shadow-none" v-model="lead.lead_priority_id"
                              @focus="$refs.popover.closePopover"
                              @change="updateLeadPriority($event, lead.id, lead.lead_priority_id)" data-bs-toggle="popover"
                              tabindex="-1">
                              <option v-for="priority in leadPriorityTypes" :key="priority.id" :value="priority.id">
                                 {{ priority.display_name }}
                              </option>
                           </select>
                        </td>

                        <!-- Status Cell -->
                        <td style="position: relative; text-align: center;">
                           <span class="spark-pill-badge" :class="getLeadStatusBadgeClass(lead.lead_status_id)">
                              <i :class="getLeadStatusIcon(lead.lead_status_id)"></i>
                              {{ getStatusDisplayName(lead.lead_status_id) }}
                           </span>

                           <select class="form-select table-select-status shadow-none" v-model="lead.lead_status_id"
                              @focus="$refs.popover.closePopover"
                              @change="leadStatusChange($event, lead.id, lead.lead_status_id)" data-bs-toggle="popover"
                              tabindex="-1">
                              <option v-for="status in leadStatusTypes" :key="status.id" :value="status.id"
                                 :disabled="getLeadStatusId('ProposalSent') == status.id">
                                 {{ status.display_name }}
                              </option>
                           </select>
                        </td>

                        <!-- Assigned BDM Cell -->
                        <td>
                           <div v-if="lead.assigned_user_id && lead.assigned_user_id !== 0" class="d-flex align-items-center gap-2">
                              <span class="user-initials-icon-sm" :style="{ backgroundColor: getBdmColor(getBdmName(lead.assigned_user_id)) }">
                                 {{ getBdmInitials(getBdmName(lead.assigned_user_id)) }}
                              </span>
                              <div class="dropdown">
                                 <button type="button" class="btn btn-link text-decoration-none p-0 fs-sm fw-500 text-dark text-truncate d-flex align-items-center gap-1 dropdown-toggle no-arrow border-0 bg-transparent hover-underline"
                                    data-bs-toggle="dropdown" aria-expanded="false" :title="'Current: ' + getBdmName(lead.assigned_user_id) + ' (Click to reassign)'" style="max-width: 140px;">
                                    <span class="text-truncate">{{ getBdmName(lead.assigned_user_id) }}</span>
                                    <i class="fa-solid fa-chevron-down text-muted" style="font-size: 0.65rem;"></i>
                                 </button>
                                 <ul class="dropdown-menu shadow-lg border-0 py-1 modern-action-dropdown" style="max-height: 240px; overflow-y: auto;">
                                    <li class="dropdown-header text-uppercase fs-xs fw-bold text-muted py-1">Reassign BDM</li>
                                    <li v-for="b in bdms" :key="b.id || b.auth_user_id">
                                       <a class="dropdown-item fs-sm d-flex align-items-center gap-2 cursor-pointer py-1"
                                          :class="{ 'active text-white': isBdmSelected(lead, b) }"
                                          @click.prevent="assignBdmDirect(lead, b)">
                                          <span class="user-initials-icon-xs" :style="{ backgroundColor: getBdmColor(getBdmItemName(b)) }">{{ getBdmInitials(getBdmItemName(b)) }}</span>
                                          <span>{{ getBdmItemName(b) }}</span>
                                       </a>
                                    </li>
                                 </ul>
                              </div>
                           </div>
                           <div v-else>
                              <div class="dropdown">
                                 <button type="button" class="spark-pill-badge spark-pill-badge--unsent-edits cursor-pointer dropdown-toggle no-arrow border-0"
                                    data-bs-toggle="dropdown" aria-expanded="false"
                                    title="No BDM assigned. Click to assign a BDM instantly.">
                                    <i class="fa-solid fa-triangle-exclamation"></i> Assign BDM <i class="fa-solid fa-chevron-down ms-1" style="font-size: 0.65rem;"></i>
                                 </button>
                                 <ul class="dropdown-menu shadow-lg border-0 py-1 modern-action-dropdown" style="max-height: 240px; overflow-y: auto;">
                                    <li class="dropdown-header text-uppercase fs-xs fw-bold text-muted py-1">Select BDM</li>
                                    <li v-for="b in bdms" :key="b.id || b.auth_user_id">
                                       <a class="dropdown-item fs-sm d-flex align-items-center gap-2 cursor-pointer py-1"
                                          @click.prevent="assignBdmDirect(lead, b)">
                                          <span class="user-initials-icon-xs" :style="{ backgroundColor: getBdmColor(getBdmItemName(b)) }">{{ getBdmInitials(getBdmItemName(b)) }}</span>
                                          <span>{{ getBdmItemName(b) }}</span>
                                       </a>
                                    </li>
                                 </ul>
                              </div>
                           </div>
                        </td>

                        <!-- Created Date Cell -->
                        <td @click="onButtonClick('view', lead.id)" data-bs-toggle="offcanvas" data-bs-target="#leadSidebar" aria-controls="leadSidebar" class="cursor-pointer">
                           <div class="d-flex flex-column">
                              <span class="fs-sm fw-500 text-dark">{{ formatDate(lead.date_created) }}</span>
                              <span class="text-muted fs-xs">{{ formatRelativeTime(lead.date_created) }}</span>
                           </div>
                        </td>

                        <!-- Action Column (Circular Icon Buttons + Circular 3-Dot More Actions) -->
                        <td style="text-align: right;" @click.stop>
                           <div class="d-flex align-items-center justify-content-end gap-2">
                              <!-- 1) Proposal Action (Circular Icon only) -->
                              <button type="button" 
                                 class="action-btn-circle action-btn-circle--proposal position-relative"
                                 :disabled="!allowedStatusIds.includes(lead.lead_status_id) || !lead.assigned_user_id || lead.assigned_user_id === 0"
                                 :title="(!lead.assigned_user_id || lead.assigned_user_id === 0) ? 'Cannot create proposal: BDM not assigned' : ((lead.lead_proposals && lead.lead_proposals.length > 0) ? 'View / Manage Proposal' : 'Create Proposal')"
                                 @click="openProposalModal(lead.id, lead)">
                                 <i class="fa-solid fa-file-signature"></i>
                                 <span v-if="lead.lead_proposals && lead.lead_proposals.length > 0" class="action-btn-dot bg-success" title="Proposal exists"></span>
                              </button>

                              <!-- 2) View / Edit Details Action (Circular Icon only) -->
                              <button type="button" 
                                 class="action-btn-circle action-btn-circle--edit"
                                 title="View / Edit Details"
                                 @click="onButtonClick('view', lead.id)" 
                                 data-bs-toggle="offcanvas" 
                                 data-bs-target="#leadSidebar" 
                                 aria-controls="leadSidebar">
                                 <i class="fa-solid fa-pen-to-square"></i>
                              </button>

                              <!-- 3) 3-Dot More Actions Menu -->
                              <div class="dropdown">
                                 <button type="button" class="btn-more-actions" data-bs-toggle="dropdown" aria-expanded="false" title="More Actions">
                                    <i class="fa-solid fa-ellipsis-vertical"></i>
                                 </button>
                                 <ul class="dropdown-menu dropdown-menu-end shadow-lg border-0 py-2 modern-action-dropdown">

                                    <!-- Upload Legacy Proposal -->
                                    <li>
                                       <a class="dropdown-item d-flex align-items-center gap-2 cursor-pointer"
                                          data-bs-toggle="modal" data-bs-target="#leadProposals"
                                          @click="onButtonClick('proposal', lead.id)">
                                          <i class="fa-solid fa-file-arrow-up text-primary"></i> Upload Legacy Proposal
                                          <i v-if="lead.has_uploaded_proposal" class="fa-solid fa-circle-check text-success ms-auto fs-xs"></i>
                                       </a>
                                    </li>

                                    <!-- Preview Proposal Page -->
                                    <li>
                                       <a class="dropdown-item d-flex align-items-center gap-2"
                                          :class="{ 'disabled text-muted': !lead.lead_proposals || lead.lead_proposals.length === 0, 'cursor-pointer': lead.lead_proposals && lead.lead_proposals.length > 0 }"
                                          @click="(lead.lead_proposals && lead.lead_proposals.length > 0) ? onButtonClick('preview', lead.lead_proposals) : null">
                                          <i class="fa-solid fa-globe text-info"></i> View Client Proposal Page
                                       </a>
                                    </li>

                                    <li><hr class="dropdown-divider my-1"></li>

                                    <!-- Link / Unlink Organisation -->
                                    <li v-if="lead.organisation_uuid || (lead.organisation && lead.organisation.uuid)">
                                       <a class="dropdown-item d-flex align-items-center gap-2 cursor-pointer"
                                          :class="{ 'disabled text-muted': isUnlinkDisabled(lead) }"
                                          @click="!isUnlinkDisabled(lead) ? unlinkOrganisationConfirm(lead.uuid || lead.id, lead) : null">
                                          <i class="fas fa-unlink text-danger"></i> Unlink Organisation
                                       </a>
                                    </li>
                                    <li v-else>
                                       <a class="dropdown-item d-flex align-items-center gap-2 cursor-pointer"
                                          :class="{ 'disabled text-muted': isAuthSynced(lead) }"
                                          @click="!isAuthSynced(lead) ? openLinkOrgModal(lead) : null">
                                          <i class="fas fa-link text-primary"></i> Link to Organisation
                                       </a>
                                    </li>

                                    <!-- Delete Lead -->
                                    <li>
                                       <a class="dropdown-item d-flex align-items-center gap-2 text-danger"
                                          :class="{ 'disabled text-muted opacity-50': (lead.lead_proposals && lead.lead_proposals.length > 0) || isAuthSynced(lead), 'cursor-pointer': !(lead.lead_proposals && lead.lead_proposals.length > 0) && !isAuthSynced(lead) }"
                                          @click="(!(lead.lead_proposals && lead.lead_proposals.length > 0) && !isAuthSynced(lead)) ? onButtonClick('delete', lead.id) : null">
                                          <i class="fa-solid fa-trash-can"></i> Delete Lead
                                       </a>
                                    </li>
                                 </ul>
                              </div>
                           </div>
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>

         </div>
      </div>

      <!-- ── Modals & Offcanvas Integrations ── -->
      <!-- Create / Edit Lead Modal -->
      <div class="modal fade modal-blur" id="leadForm" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="leadForm" style="top: 2%;">
         <div class="blur-backdrop"></div>
         <div ref="modalDialogRef" class="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-lead-form-width">
            <div class="modal-content" style="border-radius: 16px; border: none; box-shadow: 0 5px 15px rgba(0,0,0,0.3); overflow: visible; background-color: #fff;">
               <button ref="closeModal" type="button" class="close-button-custom" data-bs-dismiss="modal"
                  @click="this.$refs.leadFormRef.resetForm()" aria-label="Close">&times;</button>
               <div class="px-4 pt-4 pb-0 text-start">
                  <h5 class="modal-title-custom" style="color: #364A63; font-weight: bold; font-size: 1.25rem;">Create Lead</h5>
                  <hr style="margin-top: 1rem; margin-bottom: 0.5rem; border-color: #dee2e6;">
               </div>
               <div class="modal-body px-4 pb-4 pt-2">
                  <LeadForm ref="leadFormRef" @onSubmit="init" />
               </div>
            </div>
         </div>
      </div>

      <!-- Legacy Proposals Modal -->
      <div class="modal fade" id="leadProposals" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="leadProposalsModalRef" style="top: 2%">
         <div ref="leadProposalModalDialogRef" class="modal-dialog modal-dialog-scrollable modal-lg">
            <div class="modal-content">
               <div class="modal-header d-flex justify-content-between align-items-start">
                  <div class="d-flex align-items-start">
                     <h3 class="modal-title" style="color:#f4fbff"><strong> Proposals </strong></h3>
                  </div>
                  <div class="d-flex align-items-end">
                     <button ref="closeLeadProposalModal" type="button" class="btn-close" data-bs-dismiss="modal"
                        aria-label="Close" @click="this.$refs.leadProposalsRef.resetForm()"></button>
                  </div>
               </div>
               <Proposal ref="leadProposalsRef" @onUpdate="init" @onClose="init" :isProposalSentStatus="isLeadStatusProposalSent" />
            </div>
         </div>
      </div>

      <!-- Vertex Proposal Modal -->
      <div class="modal fade modal-blur" id="leadVertexProposals" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="leadVertexProposalsModalRef" style="top: 2%">
         <div class="blur-backdrop"></div>
         <div ref="leadVertexProposalModalDialogRef" class="modal-dialog modal-dialog-scrollable modal-custom-width">
            <div class="modal-content" style="border-radius: 16px; border: none; box-shadow: 0 10px 30px rgba(0,0,0,0.25); background-color: #fff; min-height: 600px; max-height: 92vh;">
               <button ref="closeLeadVertexProposalModal" type="button" class="close-button-custom" @click="closeProposalModal" aria-label="Close">&times;</button>
               <div class="px-4 pt-4 pb-0 text-start">
                  <h5 class="modal-title-custom" style="color: #364A63; font-weight: bold; font-size: 1.25rem;">Create Proposal</h5>
                  <hr style="margin-top: 1rem; margin-bottom: 0.5rem; border-color: #dee2e6;">
               </div>
               <div class="modal-body px-4 pb-4 pt-2">
                  <VertexProposal :lead_id="selectedLeadID" ref="vertexLeadProposalsRef" />
               </div>
            </div>
         </div>
      </div>

      <!-- Offcanvas Lead Details Sidebar -->
      <div class="offcanvas offcanvas-end" tabindex="-1" id="leadSidebar" aria-labelledby="leadSidebar" ref="leadSidebarRef" style="width:700px">
         <router-view @onSubmit="getLeadsList()"></router-view>
      </div>

      <div id="popover" class="d-none" role="tooltip">
         <Popover ref="popover" @cancelChange="getLeadsList()" @onUploadProposal="toggleProposalUpload" />
      </div>

      <!-- Activity Timeline Modal -->
      <div class="modal fade" id="leadActivityModal" ref="leadActivityModalRef" tabindex="-1">
         <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg modal-blur">
            <div class="modal-content modern-modal-content">
               <div class="modern-activity-modal-header">
                  <div class="d-flex align-items-center gap-3">
                     <div class="modal-header-icon-badge">
                        <i class="fa-solid fa-clock-rotate-left text-primary"></i>
                     </div>
                     <div>
                        <h5 class="modal-title mb-0 fw-bold">Lead Activity Timeline</h5>
                        <p class="modal-subtitle text-muted mb-0 small">
                           Real-time audit log of all events for this lead
                        </p>
                     </div>
                  </div>
                  <button type="button" class="modern-modal-close-btn" data-bs-dismiss="modal" aria-label="Close">
                     <i class="fa-solid fa-xmark"></i>
                  </button>
               </div>
               <div class="modal-body modern-activity-modal-body p-0">
                  <LeadNotesForm ref="leadNotesFormRef" />
               </div>
               <div class="modal-footer modern-activity-modal-footer">
                  <button type="button" class="btn btn-light btn-sm fw-semibold rounded-pill px-3" data-bs-dismiss="modal">
                     Close
                  </button>
               </div>
            </div>
         </div>
      </div>

      <!-- Confirmation Modal -->
      <ConfirmModal
         v-model="showConfirmModal"
         :title="confirmModalTitle"
         :message="confirmModalMessage"
         @confirm="onConfirmAction"
      />

      <!-- Link Organisation Modal -->
      <Transition name="lead-modal-fade">
         <div v-if="showLinkOrgModal" class="lead-modal-overlay" @click.self="closeLinkOrgModal">
            <div class="lead-modal">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title"><i class="fas fa-link" style="color: #00A0C1;"></i> Link to Organisation</h5>
                  <button type="button" @click="closeLinkOrgModal" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <form @submit.prevent="confirmLinkOrg" class="lead-modal__body">
                  <div class="lead-form-group">
                     <label class="lead-form-label">
                        Select an organisation to link to <strong>{{ leadToLink?.organisation_name || (leadToLink?.first_name ? leadToLink?.first_name + ' ' + leadToLink?.last_name : '') }}</strong> <span class="lead-required">*</span>
                     </label>
                     <select class="lead-form-input" v-model="selectedOrgUuid" required>
                        <option disabled value="">- Select Organisation -</option>
                        <option v-for="org in organisationsList" :key="org.id || org.uuid" :value="org.id || org.uuid">
                           {{ org.name }}
                        </option>
                     </select>
                  </div>
                  <div class="lead-modal__actions">
                     <button type="button" class="lead-btn lead-btn--ghost" @click="closeLinkOrgModal">Cancel</button>
                     <button type="submit" class="lead-btn lead-btn--primary" :disabled="!selectedOrgUuid">
                        <i class="fas fa-link me-1"></i> Link Organisation
                     </button>
                  </div>
               </form>
            </div>
         </div>
      </Transition>

      <!-- Filter Settings Modal -->
      <Transition name="lead-modal-fade">
         <div v-if="showFilterSettingsModal" class="lead-modal-overlay" @click.self="showFilterSettingsModal = false">
            <div class="lead-modal" style="max-width: 450px;">
               <div class="lead-modal__header">
                  <h5 class="lead-modal__title"><i class="fas fa-sliders"></i> Filter Settings</h5>
                  <button type="button" @click="showFilterSettingsModal = false" class="lead-modal__close">&times;</button>
               </div>
               <div class="lead-modal__divider"></div>
               <div class="lead-modal__body">
                  <!-- Exclude Test Data Switch -->
                  <div class="d-flex align-items-center justify-content-between mb-4">
                     <div>
                        <h6 class="mb-0" style="font-weight: 600; color: #2D3748;">Exclude Test Data</h6>
                        <small class="text-muted">Hide leads marked as test data</small>
                     </div>
                     <div class="form-check form-switch mb-0" style="padding-left: 0;">
                        <input v-model="excludeTestData" type="checkbox" class="form-check-input custom-switch-input mb-0" id="modalExcludeTestDataSwitch" style="cursor: pointer; width: 2.5em; height: 1.25em; margin-left: 0;">
                     </div>
                  </div>

                  <!-- Exclude Spam, Lost, Cold & Test Only Switch -->
                  <div class="d-flex align-items-center justify-content-between mb-3">
                     <div>
                        <h6 class="mb-0" style="font-weight: 600; color: #2D3748;">Exclude Spam, Lost, Cold & Test Only</h6>
                        <small class="text-muted">Hide leads with these status classifications</small>
                     </div>
                     <div class="form-check form-switch mb-0" style="padding-left: 0;">
                        <input v-model="excludeStatuses" type="checkbox" class="form-check-input custom-switch-input mb-0" id="modalExcludeStatusesSwitch" style="cursor: pointer; width: 2.5em; height: 1.25em; margin-left: 0;">
                     </div>
                  </div>

                  <div class="lead-modal__actions mt-4">
                     <button type="button" class="lead-btn lead-btn--primary w-100" @click="showFilterSettingsModal = false">Apply Filters</button>
                  </div>
               </div>
            </div>
         </div>
      </Transition>
   </div>
</template>

<script>
import { Offcanvas, Modal } from 'bootstrap';
import LeadForm from '@/components/Leads/LeadForm.vue';
import LeadNotesForm from '@/components/Leads/LeadNotesForm.vue';
import Proposal from '@/components/Proposals/Proposal.vue';
import VertexProposal from '@/components/Proposals/VertexProposal.vue';
import Popover from '@/components/Leads/Popover.vue';
import moment from "moment";
import { debounce } from '@/plugins/helper.js';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import TableEmptyState from '@/components/Utilities/TableEmptyState.vue';
import ConfirmModal from '@/components/Utilities/ConfirmModal.vue';

const HAS_EXISTING_AUTH_ORG = 1;
const MARKED_FOR_AUTH_CREATION = 2;

export default {
   components: {
      LeadForm,
      LeadNotesForm,
      Proposal,
      Popover,
      VertexProposal,
      LoadingPage,
      TableEmptyState,
      ConfirmModal,
   },

   data() {
      return {
         search: "",
         selectedLeadID: "",
         leadVertexProposalsModal: null,
         leadActivityModalInstance: null,
         leads: [],
         bdms: [],
         activeQuickTab: 'all',
         pagination: {
            pageSize: 10,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "5", value: 5 },
            { text: "10", value: 10 },
            { text: "20", value: 20 },
            { text: "50", value: 50 },
         ],
         statusFilter: [],
         priorityFilter: [],
         typeFilter: [],
         excludeTestData: localStorage.getItem('excludeTestData') !== null ? localStorage.getItem('excludeTestData') === 'true' : true,
         showFilterSettingsModal: false,
         excludeStatuses: true,
         sorting: {
            field: "created_at",
            order: "desc",
            filter: "none"
         },
         debounceFn: null,
         leadStatusTypes: [],
         leadPriorityTypes: [],
         leadTypeList: [],
         isLeadStatusProposalSent: false,
         totalProposals: 0,
         isLoading: false,
         allowedStatusIds: [],
         showConfirmModal: false,
         confirmModalTitle: '',
         confirmModalMessage: '',
         pendingCharityLeadId: null,
         pendingDeleteLeadId: null,
         showLinkOrgModal: false,
         leadToLink: null,
         selectedOrgUuid: "",
         organisationsList: [],
         pendingUnlinkLeadId: null,
         kpiCounts: {
            unassigned: 0,
            proposalsInFlight: 0,
            highPriority: 0,
         },
         colorPalette: [
            '#00A0C1', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', 
            '#10b981', '#14b8a6', '#6366f1', '#06b6d4', '#e11d48'
         ],
      };
   },

   computed: {
      isFiltered() {
         return (this.search && this.search.trim().length > 0) ||
                this.statusFilter.length > 0 ||
                this.priorityFilter.length > 0 ||
                this.typeFilter.length > 0 ||
                this.activeQuickTab !== 'all';
      },
      paginationLabel() {
         const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
         const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
         return start + " - " + end;
      },
      currentPage() { return this.pagination.page; },
      rowsPerPage() { return this.pagination.pageSize; },

      unassignedCount() {
         return this.kpiCounts.unassigned;
      },
      proposalInFlightCount() {
         return this.kpiCounts.proposalsInFlight;
      },
      highPriorityCount() {
         return this.kpiCounts.highPriority;
      },
   },

   watch: {
      currentPage() {
         this.getLeadsList();
      },
      rowsPerPage() {
         if (this.pagination.page === 1) {
            this.getLeadsList();
         } else {
            this.pagination.page = 1;
         }
      },
      statusFilter: {
         handler() {
            this.pagination.page = 1;
            this.getLeadsList();
         },
         deep: true
      },
      priorityFilter: {
         handler() {
            this.pagination.page = 1;
            this.getLeadsList();
         },
         deep: true
      },
      typeFilter: {
         handler() {
            this.pagination.page = 1;
            this.getLeadsList();
         },
         deep: true
      },
      excludeTestData(newVal) {
         localStorage.setItem('excludeTestData', newVal);
         this.pagination.page = 1;
         this.getLeadsList();
      },
      excludeStatuses() {
         this.pagination.page = 1;
         this.getLeadsList();
      },
   },

   created() {
      this.init();
      this.debounceFn = debounce(() => this.getLeadsList(), 500);
      this.HAS_EXISTING_AUTH_ORG = HAS_EXISTING_AUTH_ORG;
      this.MARKED_FOR_AUTH_CREATION = MARKED_FOR_AUTH_CREATION;
   },

   mounted() {
      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('click', this.handleClickOutsideProposal);
      this.leadVertexProposalsModal = new Modal(this.$refs.leadVertexProposalsModalRef);
      this.leadActivityModalInstance = new Modal(this.$refs.leadActivityModalRef);

      if (this.$route.params.id != null) {
         new Offcanvas(this.$refs.leadSidebarRef).show();
         this.onToggleSidebar();
         this.$router.push({ name: 'leads-v2-view', params: { id: this.$route.params.id } });
      }
   },

   beforeUnmount() {
      if (this.$refs.popover) this.$refs.popover.closePopover(this.$refs.popover);
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('click', this.handleClickOutsideProposal);
   },

   methods: {
      async init() {
         await Promise.all([
            this.getLeadStatusList(),
            this.getLeadPriorityList(),
            this.getLeadTypeList(),
            this.getBdmsList(),
         ]);
         this.getLeadsList();
         this.fetchKpiMetrics();
         if (this.$refs.closeModal) this.$refs.closeModal.click();
      },

      getHighPriorityId() {
         const highObj = this.leadPriorityTypes.find(p => (p.name || '').toLowerCase() === 'high' || (p.display_name || '').toLowerCase() === 'high');
         return highObj ? highObj.id : '';
      },

      async fetchKpiMetrics() {
         try {
            const highId = this.getHighPriorityId();
            const pendingId = this.getLeadStatusId('ProposalPending');
            const sentId = this.getLeadStatusId('ProposalSent');

            const promises = [
               this.$axios.get('/leads?pageSize=1&page=1&classification=Lead&filter=unassigned&excludeTestData=true&excludeStatuses=true'),
               highId ? this.$axios.get(`/leads?pageSize=1&page=1&classification=Lead&priority=${highId}&excludeTestData=true&excludeStatuses=true`) : Promise.resolve({ data: { total_items: 0 } }),
               pendingId ? this.$axios.get(`/leads?pageSize=1&page=1&classification=Lead&status=${pendingId}&excludeTestData=true&excludeStatuses=true`) : Promise.resolve({ data: { total_items: 0 } }),
               sentId ? this.$axios.get(`/leads?pageSize=1&page=1&classification=Lead&status=${sentId}&excludeTestData=true&excludeStatuses=true`) : Promise.resolve({ data: { total_items: 0 } }),
            ];

            const [unassignedRes, highRes, pendingRes, sentRes] = await Promise.all(promises);
            this.kpiCounts.unassigned = unassignedRes.data?.total_items || 0;
            this.kpiCounts.highPriority = highRes.data?.total_items || 0;
            this.kpiCounts.proposalsInFlight = (pendingRes.data?.total_items || 0) + (sentRes.data?.total_items || 0);
         } catch (err) {
            console.error("Error fetching KPI metrics:", err);
         }
      },

      setQuickTab(tab) {
         this.activeQuickTab = tab;
         this.pagination.page = 1;
         if (tab === 'all') {
            this.clearFilter();
         } else if (tab === 'unassigned') {
            this.statusFilter = [];
            this.priorityFilter = [];
            this.typeFilter = [];
            this.getLeadsList();
         } else if (tab === 'proposals') {
            const pendingId = this.getLeadStatusId('ProposalPending');
            const sentId = this.getLeadStatusId('ProposalSent');
            this.statusFilter = [pendingId, sentId].filter(Boolean);
            this.priorityFilter = [];
            this.typeFilter = [];
            this.getLeadsList();
         } else if (tab === 'high_priority') {
            const highPriorityId = this.getHighPriorityId();
            if (highPriorityId) {
               this.priorityFilter = [highPriorityId];
            }
            this.statusFilter = [];
            this.typeFilter = [];
            this.getLeadsList();
         }
      },

      async getBdmsList() {
         try {
            const res = await this.$axios.get('/vertex-bdm');
            if (res.data && res.data.success && res.data.bdms) {
               this.bdms = res.data.bdms;
            }
         } catch (err) {
            console.error('Error fetching BDMs in LeadV2:', err);
         }
      },

      getBdmItemName(b) {
         if (!b) return 'Unassigned';
         return b.name || `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || 'BDM';
      },

      isBdmSelected(lead, b) {
         if (!lead || !lead.assigned_user_id) return false;
         const numId = Number(lead.assigned_user_id);
         return b.auth_user_id === numId || b.id === numId || b.db_id === numId;
      },

      async assignBdmDirect(lead, bdm) {
         const targetUserId = bdm ? (bdm.auth_user_id || bdm.id) : 0;
         try {
            this.isLoading = true;
            const res = await this.$axios.put(`/leads/${lead.id}/assigned-user`, {
               assigned_user_id: targetUserId
            });
            if (res.data && res.data.success) {
               lead.assigned_user_id = targetUserId;
               this.$toast.success(`Assigned ${bdm ? this.getBdmItemName(bdm) : 'Unassigned'} to ${this.getLeadDisplayName(lead)}`);
               this.fetchKpiMetrics();
               if (this.activeQuickTab === 'unassigned') {
                  this.getLeadsList();
               }
            } else {
               this.$toast.error(res.data?.message || "Failed to update assigned user");
            }
         } catch (err) {
            this.$toast.error(err.response?.data?.message || "Error updating assigned user");
         } finally {
            this.isLoading = false;
         }
      },

      getBdmName(userId) {
         if (!userId || userId === 0) return 'Unassigned';
         const numId = Number(userId);
         const found = this.bdms.find(b => b.auth_user_id === numId || b.id === numId || b.db_id === numId);
         if (found) {
            return found.name || `${found.first_name || ''} ${found.last_name || ''}`.trim() || found.email || 'BDM';
         }
         return `User #${userId}`;
      },

      getBdmInitials(name) {
         if (!name || name === 'Unassigned') return '?';
         const parts = name.trim().split(' ');
         if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
         return parts[0].substring(0, 2).toUpperCase();
      },

      getBdmColor(name) {
         if (!name || name === 'Unassigned') return '#94a3b8';
         let hash = 0;
         for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
         return this.colorPalette[Math.abs(hash) % this.colorPalette.length];
      },

      getLeadDisplayName(lead) {
         if (lead.first_name || lead.last_name) {
            return `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
         }
         return lead.organisation_name || 'Unnamed Lead';
      },

      getLeadInitials(lead) {
         const name = this.getLeadDisplayName(lead);
         const parts = name.trim().split(' ');
         if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
         return parts[0].substring(0, 2).toUpperCase();
      },

      getLeadAvatarColor(lead) {
         const name = this.getLeadDisplayName(lead);
         let hash = 0;
         for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
         return this.colorPalette[Math.abs(hash) % this.colorPalette.length];
      },

      isSorted(field) {
         if (field === 'organisation_name') return this.sorting.field === 'organisation_name' || this.sorting.field === 'lead_name';
         if (field === 'created_at') return this.sorting.field === 'created_at' || this.sorting.field === 'id';
         if (field === 'type') return this.sorting.field === 'type' || this.sorting.field === 'lead_type';
         if (field === 'priority') return this.sorting.field === 'priority' || this.sorting.field === 'lead_priority_id';
         if (field === 'status') return this.sorting.field === 'status' || this.sorting.field === 'lead_status_id';
         if (field === 'assigned_user_id') return this.sorting.field === 'assigned_user_id' || this.sorting.field === 'bdm_id';
         return this.sorting.field === field;
      },

      getSortIcon(field) {
         if (!this.isSorted(field)) {
            return 'fa-solid fa-sort sort-icon-inactive';
         }
         return this.sorting.order === 'asc' ? 'fa-solid fa-sort-up sort-icon-active' : 'fa-solid fa-sort-down sort-icon-active';
      },

      toggleSort(field, defaultOrder = 'asc') {
         if (this.isSorted(field)) {
            this.sorting.order = this.sorting.order === 'asc' ? 'desc' : 'asc';
         } else {
            this.sorting.field = field;
            this.sorting.order = defaultOrder;
         }
         this.pagination.page = 1;
         this.getLeadsList();
      },

      async getLeadsList() {
         this.isLoading = true;
         let apiPath = `/leads?pageSize=${this.pagination.pageSize}&page=${this.pagination.page}&classification=Lead`;

         if (this.sorting.field) {
            apiPath += `&sort=${this.sorting.field}`;
            if (this.sorting.order !== "") apiPath += `&order=${this.sorting.order}`;
         }

         if (this.activeQuickTab === 'unassigned') {
            apiPath += `&filter=unassigned`;
         }

         if (this.statusFilter.length > 0) {
            apiPath += `&status=${this.statusFilter.join(",")}`;
         }
         if (this.priorityFilter.length > 0) {
            apiPath += `&priority=${this.priorityFilter.join(",")}`;
         }
         if (this.typeFilter.length > 0) {
            apiPath += `&type=${this.typeFilter.join(",")}`;
         }
         apiPath += `&excludeTestData=${this.excludeTestData}`;
         apiPath += `&excludeStatuses=${this.excludeStatuses}`;

         if (this.search !== "") {
            let encodedStr = this.search.replaceAll("+", "%2B");
            apiPath += `&query=${encodedStr}`;
         }

         try {
            const response = await this.$axios.get(apiPath);
            this.leads = response.data.leads || [];
            this.pagination.totalItems = response.data.total_items;
            this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.pageSize);
         } catch (error) {
            this.$toast.error(error.response?.data?.message || "Error fetching leads");
         } finally {
            this.isLoading = false;
         }
      },

      async exportLeadsToCSV() {
         try {
            this.isLoading = true;
            this.$toast.info("Export started. Your file will download shortly.");

            let apiPath = `/leads/export/csv?pageSize=2000&page=1&sort=${this.sorting.field || 'id'}&order=${this.sorting.order || 'desc'}&classification=Lead`;
            if (this.search) apiPath += `&query=${encodeURIComponent(this.search)}`;
            if (this.statusFilter.length > 0) apiPath += `&status=${this.statusFilter.join(",")}`;
            if (this.priorityFilter.length > 0) apiPath += `&priority=${this.priorityFilter.join(",")}`;
            if (this.typeFilter.length > 0) apiPath += `&type=${this.typeFilter.join(",")}`;
            apiPath += `&excludeTestData=${this.excludeTestData}`;
            apiPath += `&excludeStatuses=${this.excludeStatuses}`;

            const response = await this.$axios.get(apiPath, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'text/csv' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `leads_export_${moment().format('YYYYMMDD_HHmm')}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
         } catch (error) {
            this.$toast.error("An error occurred during export.");
         } finally {
            this.isLoading = false;
         }
      },

      getFormerLeadOrgName(lead) {
         if (!lead) return '';
         if (lead.original_organisation_name && lead.original_organisation_name !== lead.organisation_name) {
            return lead.original_organisation_name;
         }
         if (lead.organisation && lead.organisation.original_organisation_name && lead.organisation.original_organisation_name !== lead.organisation_name) {
            return lead.organisation.original_organisation_name;
         }
         return '';
      },

      isUnlinkDisabled() { return false; },
      getUnlinkTooltip(lead) {
         if (!lead) return '';
         const linkedOrgName = (lead.organisation && lead.organisation.name) ? lead.organisation.name : lead.organisation_name;
         return `Unlink ${linkedOrgName} (restores original lead details)`;
      },
      isAuthSynced(lead) {
         if (!lead) return false;
         return !!(lead.is_auth_synced || lead.has_auth_organisation === 1 || lead.has_auth_organisation === 2 || (lead.organisation && (lead.organisation.is_auth_synced || lead.organisation.has_auth_organisation === 1 || lead.organisation.has_auth_organisation === 2)));
      },
      isClient(lead) {
         if (!lead) return false;
         if (lead.classification === 'Client' || lead.organisation_classification === 'Client') return true;
         if (lead.organisation && lead.organisation.classifications) {
            return lead.organisation.classifications.some(c => (c.classification === 'Client' || c === 'Client'));
         }
         return false;
      },

      openProposalModal(leadId, lead) {
         const hasExistingProposals = lead && lead.lead_proposals && lead.lead_proposals.length > 0;
         const isClientOrg = this.isClient(lead);
         const isSynced = this.isAuthSynced(lead);

         if (isClientOrg && hasExistingProposals && !isSynced) {
            this.$toast.error("Proposals can only be created for Client organisations that are synced with Auth.");
            return;
         }

         if (lead && lead.is_charity) {
            this.pendingCharityLeadId = leadId;
            this.confirmModalTitle = 'Charity Notice';
            this.confirmModalMessage = 'Please be advised that this organisation is a registered charity and differentiated pricing may be required.';
            this.showConfirmModal = true;
         } else {
            this.$refs.vertexLeadProposalsRef.init(leadId);
            this.leadVertexProposalsModal.show();
         }
      },

      onConfirmAction() {
         if (this.pendingCharityLeadId) {
            const leadId = this.pendingCharityLeadId;
            this.pendingCharityLeadId = null;
            if (!leadId) return;
            this.$refs.vertexLeadProposalsRef.init(leadId);
            this.leadVertexProposalsModal.show();
         } else if (this.pendingUnlinkLeadId) {
            const leadId = this.pendingUnlinkLeadId;
            this.pendingUnlinkLeadId = null;
            this.unlinkOrganisation(leadId);
         } else if (this.pendingDeleteLeadId) {
            const leadId = this.pendingDeleteLeadId;
            this.pendingDeleteLeadId = null;
            this.executeDeleteLead(leadId);
         }
      },

      closeProposalModal() {
         this.leadVertexProposalsModal.hide();
         this.selectedLeadID = null;
      },

      handleClickOutside(event) {
         if (!document.body.contains(event.target)) return;
         if (event.target.closest && (event.target.closest('.vti__dropdown-list') || event.target.closest('.vue-tel-input') || event.target.closest('.multiselect'))) return;

         if (this.$refs.modalDialogRef && !this.$refs.modalDialogRef.contains(event.target) && this.$refs.leadForm.classList.contains('show')) {
            const closeButton = this.$refs.closeModal;
            if (closeButton) {
               this.$refs.leadFormRef.resetForm();
               closeButton.click();
            }
         }
      },

      handleClickOutsideProposal(event) {
         if (!document.body.contains(event.target)) return;
         if (event.target.closest && event.target.closest('.multiselect')) return;
         
         if (this.$refs.leadProposalModalDialogRef && !this.$refs.leadProposalModalDialogRef.contains(event.target) && this.$refs.leadProposalsModalRef.classList.contains('show')) {
            const closeButton = this.$refs.closeLeadProposalModal;
            if (closeButton) closeButton.click();
         }
      },

      onButtonClick(action, id) {
         if (action === "create") this.$refs.leadFormRef.isCreate();
         else if (action === "edit") this.$refs.leadFormRef.isEdit(id);
         else if (action === "delete") {
            const lead = this.leads.find(l => l.id === id);
            if (lead) this.deleteLeadConfirm(lead);
         }
         else if (action === "view") {
            this.onToggleSidebar();
            this.$router.push({ name: 'leads-v2-view', params: { id: id } });
         } else if (action === "proposal") this.$refs.leadProposalsRef.init(id);
         else if (action === "preview") {
            let lead_proposals = id;
            let internal_key = lead_proposals[lead_proposals.length - 1].internal_key;
            let ukey = lead_proposals[lead_proposals.length - 1].unique_key;
            const routeData = this.$router.resolve({ name: 'clientProposal', params: { unique_key: ukey, access_key: internal_key } });
            window.open(routeData.href, '_blank');
         }
      },

      onToggleSidebar() {
         this.$refs.leadSidebarRef.addEventListener('hidden.bs.offcanvas', () => {
            this.$router.push({ name: 'leads_v2' });
         });
      },

      onSearch() {
         this.debounceFn();
         this.pagination.page = 1;
      },
      clearSearch() {
         this.search = '';
         this.pagination.page = 1;
         this.getLeadsList();
      },

      toggleProposalUpload({ id }) {
         this.isLeadStatusProposalSent = true;
         this.$refs.leadProposalsRef.showModal(this.$refs.leadProposals);
         this.$refs.leadProposals.addEventListener('hidden.bs.modal', () => { this.isLeadStatusProposalSent = false; });
         this.$refs.leadProposalsRef.init(id);
      },

      formatDate(date) { return date ? moment(date).format("DD-MMM-YYYY") : '-'; },
      formatRelativeTime(date) { return date ? moment(date).fromNow() : ''; },
      previousPage() { if (this.pagination.page > 1) this.pagination.page -= 1; },
      nextPage() { if (this.pagination.totalPages > this.pagination.page) this.pagination.page += 1; },

      async leadStatusChange(e, leadId, statusId) {
         const specificStatuses = ['Spam', 'test_only', 'PartnerLead', 'Recruitment', 'pr_opportunity'];
         const isSpecific = specificStatuses.some(status => statusId == this.getLeadStatusId(status));
         
         if (isSpecific) {
            let lead = this.leads.find(l => l.id == leadId);
            let naPriority = this.leadPriorityTypes.find(p => p.name === 'not_applicable');
            if (lead && naPriority) lead.lead_priority_id = naPriority.id;
         }

         if (statusId == this.getLeadStatusId('ProposalSent')) {
            await this.$refs.leadProposalsRef.hasUploadedProposal(leadId).then((response) => this.totalProposals = response);
         }
         if (statusId == this.getLeadStatusId('Lost')) this.$refs.popover.showPopover(e.target, leadId, statusId, "lostLead");
         else if (statusId == this.getLeadStatusId('ProposalSent') && this.totalProposals < 1) this.$refs.popover.showPopover(e.target, leadId, statusId, "proposalSent");
         else this.$refs.popover.showPopover(e.target, leadId, statusId, "statusUpdate");
      },

      getLeadStatusId(statusName) {
         return this.leadStatusTypes.filter(e => e.name === statusName).map(e => e.id).toString();
      },

      isLeadStatusSpam(lead) {
         const spamStatus = this.leadStatusTypes.find(s => s.name === 'Spam');
         return !!(spamStatus && lead.lead_status_id == spamStatus.id);
      },

      async updateLeadPriority(e, leadId, priorityId) {
         this.$refs.popover.showPopover(e.target, leadId, '', false);
         let lead = { lead_priority_id: priorityId };
         await this.$axios.put(`/leads/${leadId}/priority`, lead)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message);
                  this.getLeadsList();
               } else this.$toast.error(response.data.message);
            })
            .catch((error) => { this.$toast.error(error.response?.data?.message || "Failed to update priority"); });
      },

      async getLeadStatusList() {
         await this.$axios.get("/categories?name=LeadStatus")
            .then((response) => {
               this.leadStatusTypes = response.data.category.subcategories;
               this.allowedStatusIds = [
                  this.getLeadStatusId('New'),
                  this.getLeadStatusId('ProposalPending'),
                  this.getLeadStatusId('test_only'),
                  this.getLeadStatusId('TestOnly'),
                  this.getLeadStatusId('PartnerLead'),
                  this.getLeadStatusId('Recruitment'),
                  this.getLeadStatusId('pr_opportunity'),
               ];
            })
            .catch((error) => { this.$toast.error(error.response?.data?.message || "Failed to load statuses"); });
      },

      async getLeadPriorityList() {
         await this.$axios.get("/categories?name=LeadPriority")
            .then((response) => { this.leadPriorityTypes = response.data.category.subcategories; })
            .catch((error) => { this.$toast.error(error.response?.data?.message || "Failed to load priorities"); });
      },

      async getLeadTypeList() {
         await this.$axios.get("/categories?name=LeadType")
            .then((response) => { this.leadTypeList = response.data.category.subcategories; })
            .catch((error) => { this.$toast.error(error.response?.data?.message || "Failed to load lead types"); });
      },

      async unlinkOrganisation(leadId) {
         let lead = { organisation_uuid: "" };
         await this.$axios.put(`/leads/${leadId}/organisation`, lead)
            .then((response) => {
               if (response.data.success) { this.$toast.success(response.data.message); this.getLeadsList(); }
               else this.$toast.error(response.data.message);
            })
            .catch((error) => { this.$toast.error(error.response?.data?.message || "Failed to unlink organisation"); });
      },

      clearFilter() {
         this.statusFilter = [];
         this.priorityFilter = [];
         this.typeFilter = [];
         this.activeQuickTab = 'all';
      },
      clearAllFiltersAndSearch() {
         this.search = '';
         this.clearFilter();
         this.pagination.page = 1;
         this.getLeadsList();
      },
      openCreateLeadModal() {
         this.onButtonClick('create', '');
         const modalEl = document.getElementById('leadForm');
         if (modalEl) {
            const modal = Modal.getOrCreateInstance(modalEl);
            modal.show();
         }
      },
      removeStatusFilter(val) { this.statusFilter = this.statusFilter.filter(v => v !== val); },
      removePriorityFilter(val) { this.priorityFilter = this.priorityFilter.filter(v => v !== val); },
      removeTypeFilter(val) { this.typeFilter = this.typeFilter.filter(v => v !== val); },

      getStatusDisplayName(id) {
         const obj = this.leadStatusTypes.find(o => o.id === id);
         return obj ? obj.display_name : id;
      },
      getPriorityDisplayName(id) {
         const obj = this.leadPriorityTypes.find(o => o.id === id);
         return obj ? obj.display_name : id;
      },
      getTypeDisplayName(id) {
         const obj = this.leadTypeList.find(o => o.id === id);
         return obj ? obj.display_name : id;
      },

      getPriorityName(priorityId) {
         const priority = this.leadPriorityTypes.find(p => p.id === priorityId);
         return priority ? priority.display_name : 'Unknown';
      },

      getLeadPriorityBadgeClass(priorityId) {
         const priority = this.leadPriorityTypes.find(p => p.id === priorityId);
         if (!priority) return 'spark-pill-badge--priority-na';
         const name = (priority.name || '').toLowerCase();
         if (name.includes('high')) return 'spark-pill-badge--priority-high';
         if (name.includes('medium')) return 'spark-pill-badge--priority-medium';
         if (name.includes('low')) return 'spark-pill-badge--priority-low';
         return 'spark-pill-badge--priority-na';
      },

      getLeadPriorityIcon(priorityId) {
         const priority = this.leadPriorityTypes.find(p => p.id === priorityId);
         if (!priority) return 'fa-solid fa-minus';
         const name = (priority.name || '').toLowerCase();
         if (name.includes('high')) return 'fa-solid fa-fire';
         if (name.includes('medium')) return 'fa-solid fa-equals';
         if (name.includes('low')) return 'fa-solid fa-arrow-down';
         return 'fa-solid fa-minus';
      },

      getLeadStatusBadgeClass(statusId) {
         const status = this.leadStatusTypes.find(s => s.id === statusId);
         if (!status) return 'spark-pill-badge--cold';
         const name = status.name || '';
         switch (name) {
            case 'New': return 'spark-pill-badge--new-lead';
            case 'ProposalPending': return 'spark-pill-badge--proposal-pending';
            case 'ProposalSent': return 'spark-pill-badge--proposal-sent';
            case 'ProposalAccepted': return 'spark-pill-badge--proposal-accepted';
            case 'ContractPending': return 'spark-pill-badge--contract-pending';
            case 'Converted': return 'spark-pill-badge--converted';
            case 'Lost': return 'spark-pill-badge--lost';
            case 'Cold': return 'spark-pill-badge--cold';
            case 'Spam': return 'spark-pill-badge--spam';
            case 'test_only': return 'spark-pill-badge--test-only';
            case 'PartnerLead': return 'spark-pill-badge--partner-lead';
            case 'Recruitment': return 'spark-pill-badge--recruitment';
            case 'pr_opportunity': return 'spark-pill-badge--pr-opportunity';
            default: return 'spark-pill-badge--cold';
         }
      },

      getLeadStatusIcon(statusId) {
         const status = this.leadStatusTypes.find(s => s.id === statusId);
         if (!status) return 'fa-solid fa-circle-notch';
         const name = status.name || '';
         switch (name) {
            case 'New': return 'fa-solid fa-sparkles';
            case 'ProposalPending': return 'fa-solid fa-clock';
            case 'ProposalSent': return 'fa-solid fa-paper-plane';
            case 'ProposalAccepted': return 'fa-solid fa-circle-check';
            case 'ContractPending': return 'fa-solid fa-file-contract';
            case 'Converted': return 'fa-solid fa-check-double';
            case 'Lost': return 'fa-solid fa-circle-xmark';
            case 'Cold': return 'fa-solid fa-snowflake';
            case 'Spam': return 'fa-solid fa-ban';
            case 'test_only': return 'fa-solid fa-vial';
            case 'PartnerLead': return 'fa-solid fa-handshake';
            case 'Recruitment': return 'fa-solid fa-user-group';
            case 'pr_opportunity': return 'fa-solid fa-bullhorn';
            default: return 'fa-solid fa-circle-notch';
         }
      },

      async getOrganisationsList() {
         await this.$axios.get("/organisations?pageSize=200")
            .then((response) => {
               this.organisationsList = response.data.organisations || response.data.data || [];
            })
            .catch((error) => { this.$toast.error(error.response?.data?.message || "Failed to load organisations"); });
      },

      async openLinkOrgModal(lead) {
         this.leadToLink = lead;
         this.selectedOrgUuid = "";
         if (this.organisationsList.length === 0) {
            await this.getOrganisationsList();
         }
         this.showLinkOrgModal = true;
      },

      closeLinkOrgModal() {
         this.showLinkOrgModal = false;
         this.leadToLink = null;
         this.selectedOrgUuid = "";
      },

      async confirmLinkOrg() {
         if (!this.selectedOrgUuid) return;
         const leadId = this.leadToLink.uuid || this.leadToLink.id;
         let payload = { organisation_uuid: this.selectedOrgUuid };
         
         await this.$axios.put(`/leads/${leadId}/organisation`, payload)
            .then((response) => {
               if (response.data.success) { 
                  this.$toast.success(response.data.message); 
                  this.getLeadsList(); 
               } else this.$toast.error(response.data.message);
            })
            .catch((error) => { this.$toast.error(error.response?.data?.message || "Failed to link organisation"); });
            
         this.closeLinkOrgModal();
      },

      unlinkOrganisationConfirm(leadId, lead) {
         this.pendingUnlinkLeadId = lead.uuid || lead.id || leadId;
         this.confirmModalTitle = 'Unlink Organisation';
         const orgName = (lead.organisation ? lead.organisation.name : lead.organisation_name) || 'the organisation';
         this.confirmModalMessage = `Are you sure you want to unlink ${orgName} from this lead?`;
         this.showConfirmModal = true;
      },

      deleteLeadConfirm(lead) {
         this.pendingDeleteLeadId = lead.uuid || lead.id;
         this.confirmModalTitle = 'Delete Lead';
         if (lead.organisation && !this.isAuthSynced(lead)) {
            const orgName = lead.organisation.name || lead.organisation_name || 'its linked organisation';
            this.confirmModalMessage = `Are you sure you want to delete this lead? This will also delete "${orgName}", which is a linked non-auth synced organisation.`;
         } else {
            this.confirmModalMessage = 'Are you sure you want to delete this lead?';
         }
         this.showConfirmModal = true;
      },

      async executeDeleteLead(leadId) {
         await this.$axios.delete(`/leads/${leadId}`)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message);
                  this.getLeadsList();
               } else {
                  this.$toast.error(response.data.message);
               }
            })
            .catch((error) => {
               this.$toast.error(error.response?.data?.message || "Failed to delete lead");
            });
      },
   }
}
</script>

<style scoped>
/* ── Leads V2 Workspace Styling ── */
.leads-page-v2 {
   color: #1e293b;
   font-family: inherit;
}

.fs-xs { font-size: 0.75rem; }
.fs-sm { font-size: 0.85rem; }
.fw-500 { font-weight: 500; }

.bg-teal { background-color: #00A0C1 !important; }
.text-teal { color: #00A0C1 !important; }
.bg-teal-soft { background-color: rgba(0, 160, 193, 0.1) !important; }

.text-blue { color: #3b82f6 !important; }
.bg-blue-soft { background-color: rgba(59, 130, 246, 0.1) !important; }

.text-emerald { color: #10b981 !important; }
.bg-emerald-soft { background-color: rgba(16, 185, 129, 0.1) !important; }

.text-amber { color: #d97706 !important; }
.text-amber-muted { color: #b45309 !important; font-size: 0.75rem; }
.bg-amber-soft { background-color: rgba(245, 158, 11, 0.1) !important; }

.text-indigo { color: #6366f1 !important; }
.bg-indigo-soft { background-color: rgba(99, 102, 241, 0.1) !important; }

.text-rose { color: #e11d48 !important; }
.bg-rose-soft { background-color: rgba(239, 68, 68, 0.1) !important; }

.bg-primary-soft {
   background-color: rgba(0, 160, 193, 0.1) !important;
   color: #008298 !important;
}

.modern-action-btn {
   height: 38px;
   padding: 0 16px;
   font-size: 0.85rem;
   font-weight: 600;
   border-radius: 8px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   transition: all 0.2s ease;
}

/* ── Modern Table Card & Workspace ── */
.modern-table-card {
   border-radius: 14px;
   background: #ffffff;
}

/* ── Modern Tabs ── */
.modern-tabs {
   display: flex;
   gap: 6px;
   flex-wrap: wrap;
}

.modern-tab {
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   border-radius: 8px;
   padding: 6px 14px;
   font-size: 0.85rem;
   font-weight: 600;
   color: #64748b;
   cursor: pointer;
   transition: all 0.2s ease;
   display: inline-flex;
   align-items: center;
}

.modern-tab:hover {
   background: #f1f5f9;
   color: #334155;
}

.modern-tab.active {
   background: #00A0C1;
   color: #ffffff;
   border-color: #00A0C1;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
}

.modern-tab.active i {
   color: #ffffff !important;
}

.modern-tab.active .badge {
   background-color: rgba(255, 255, 255, 0.25) !important;
   color: #ffffff !important;
}

/* ── Search & Filter Omnibar ── */
.modern-search-wrapper {
   position: relative;
   display: flex;
   align-items: center;
}

.search-icon {
   position: absolute;
   left: 12px;
   color: #94a3b8;
   font-size: 0.85rem;
   pointer-events: none;
}

.modern-search-input {
   height: 38px;
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   padding-left: 36px;
   padding-right: 32px;
   font-size: 0.85rem;
   transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.modern-search-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.btn-clear-search {
   position: absolute;
   right: 8px;
   background: none;
   border: none;
   color: #94a3b8;
   font-size: 0.85rem;
   cursor: pointer;
   padding: 4px;
}

.btn-clear-search:hover { color: #475569; }

.modern-filter-btn {
   height: 38px;
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   background: #ffffff;
   color: #334155;
   font-weight: 600;
   font-size: 0.85rem;
   padding: 0 12px;
   display: inline-flex;
   align-items: center;
   transition: all 0.2s ease;
}

.modern-filter-btn:hover,
.modern-filter-btn[aria-expanded="true"] {
   border-color: #00A0C1;
   background: #f8fafc;
   color: #00A0C1;
}

.modern-filter-btn.has-active {
   background: #e6f7fa;
   border-color: #00A0C1;
   color: #008298;
}

.modern-select {
   border-radius: 8px;
   border: 1px solid #cbd5e1;
   height: 34px;
   font-size: 0.8rem;
   font-weight: 500;
   color: #334155;
}

.modern-select:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.15);
}

.filter-chip {
   display: inline-flex;
   align-items: center;
   gap: 6px;
   background: #e6f7fa;
   color: #008298;
   border: 1px solid #b3e5ef;
   padding: 3px 10px;
   border-radius: 16px;
   font-size: 0.75rem;
   font-weight: 600;
}

.filter-chip i {
   font-size: 0.7rem;
   transition: color 0.15s;
}

.filter-chip i:hover { color: #ef4444; }

/* ── Modern Table Data Grid ── */
.modern-table-wrapper {
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   background: #ffffff;
}

.modern-table {
   border-collapse: separate;
   border-spacing: 0;
   width: 100%;
}

.modern-table thead th {
   background-color: #f8fafc;
   color: #475569;
   font-weight: 700;
   font-size: 0.78rem;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   padding: 12px 16px;
   border-bottom: 1.5px solid #e2e8f0;
}

/* ── Sortable Column Headers ── */
.sortable-th {
   cursor: pointer;
   user-select: none;
   transition: background-color 0.15s ease, color 0.15s ease;
}

.sortable-th:hover {
   background-color: #f1f5f9 !important;
   color: #00A0C1 !important;
}

.sortable-th.sorted-th {
   color: #00A0C1 !important;
}

.sort-icon-inactive {
   color: #cbd5e1;
   font-size: 0.72rem;
   margin-left: 4px;
   opacity: 0.45;
   transition: opacity 0.15s ease, color 0.15s ease;
}

.sortable-th:hover .sort-icon-inactive {
   opacity: 0.85;
   color: #94a3b8;
}

.sort-icon-active {
   color: #00A0C1 !important;
   font-size: 0.75rem;
   margin-left: 4px;
}

.modern-row {
   transition: background-color 0.15s ease;
}

.modern-row:hover {
   background-color: #f8fafc;
}

.modern-row td {
   padding: 14px 16px !important;
   border-bottom: 1px solid #f1f5f9;
   vertical-align: middle;
}

/* ── Lead Row Elements ── */
.lead-avatar {
   width: 36px;
   height: 36px;
   border-radius: 10px;
   color: #ffffff;
   font-weight: 700;
   font-size: 0.85rem;
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.lead-primary-name {
   font-weight: 700;
   font-size: 0.95rem;
   color: #0f172a;
   transition: color 0.15s ease;
}

.modern-row:hover .lead-primary-name {
   color: #00A0C1;
}

.lead-type-badge {
   display: inline-flex;
   align-items: center;
   padding: 4px 10px;
   background: #f1f5f9;
   color: #475569;
   border-radius: 6px;
   font-size: 0.8rem;
   font-weight: 600;
}

.user-initials-icon-sm {
   width: 26px;
   height: 26px;
   border-radius: 50%;
   color: #ffffff;
   font-size: 0.72rem;
   font-weight: 700;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
}

.user-initials-icon-xs {
   width: 22px;
   height: 22px;
   border-radius: 50%;
   color: #ffffff;
   font-size: 0.65rem;
   font-weight: 700;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
}

.dropdown-toggle.no-arrow::after {
   display: none !important;
}

.hover-underline:hover {
   text-decoration: underline !important;
}

/* ── Circular Action Buttons ── */
.action-btn-circle {
   width: 34px;
   height: 34px;
   min-width: 34px;
   min-height: 34px;
   padding: 0;
   border-radius: 50% !important;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.85rem;
   border: none;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
   text-decoration: none;
   position: relative;
}

.action-btn-circle:hover:not(:disabled) {
   transform: translateY(-1px);
}

.action-btn-circle:disabled,
.action-btn-circle.disabled {
   opacity: 0.35;
   cursor: not-allowed;
   pointer-events: none;
   transform: none !important;
   box-shadow: none !important;
}

.action-btn-circle--primary,
.action-btn-circle--proposal,
.action-btn-circle--send,
.action-btn-circle--edit {
   background-color: #e6f7fa;
   color: #00A0C1;
}

.action-btn-circle--primary:hover:not(:disabled),
.action-btn-circle--proposal:hover:not(:disabled),
.action-btn-circle--send:hover:not(:disabled),
.action-btn-circle--edit:hover:not(:disabled) {
   background-color: #00A0C1;
   color: #ffffff;
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.action-btn-circle--sync {
   background-color: #f0fdfa;
   color: #0d9488;
}

.action-btn-circle--sync:hover:not(:disabled) {
   background-color: #0d9488;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(13, 148, 136, 0.3);
}

.action-btn-circle--restore {
   background-color: #fffbeb;
   color: #f59e0b;
}

.action-btn-circle--restore:hover:not(:disabled) {
   background-color: #f59e0b;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
}

.action-btn-circle--unsent {
   background-color: #fffbeb;
   color: #b45309;
}

.action-btn-circle--unsent:hover:not(:disabled) {
   background-color: #f59e0b;
   color: #ffffff;
   box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
}

.action-btn-dot {
   position: absolute;
   top: 2px;
   right: 2px;
   width: 8px;
   height: 8px;
   border-radius: 50%;
   border: 1.5px solid #ffffff;
}

.btn-action-proposal {
   height: 32px;
   padding: 0 12px;
   font-size: 0.8rem;
   font-weight: 600;
   border-radius: 8px;
   background: #e6f7fa;
   color: #008298;
   border: 1px solid rgba(0, 160, 193, 0.2);
   display: inline-flex;
   align-items: center;
   transition: all 0.15s ease;
}

.btn-action-proposal:hover:not(:disabled) {
   background: #00A0C1;
   color: #ffffff;
   box-shadow: 0 2px 6px rgba(0, 160, 193, 0.25);
}

.btn-action-proposal:disabled {
   opacity: 0.4;
   cursor: not-allowed;
}

.btn-more-actions {
   width: 34px;
   height: 34px;
   min-width: 34px;
   min-height: 34px;
   padding: 0;
   border-radius: 50% !important;
   background: #f8fafc;
   border: 1px solid #e2e8f0;
   color: #64748b;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 0.85rem;
   cursor: pointer;
   transition: all 0.2s ease-in-out;
}

.btn-more-actions:hover {
   background: #f1f5f9;
   color: #00A0C1;
   border-color: #cbd5e1;
   transform: translateY(-1px);
   box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.modern-action-dropdown {
   min-width: 220px;
   border-radius: 10px;
}

.modern-action-dropdown .dropdown-item {
   padding: 8px 16px;
   font-size: 0.85rem;
   font-weight: 500;
   color: #334155;
   transition: all 0.15s ease;
}

.modern-action-dropdown .dropdown-item:hover {
   background-color: #f8fafc;
   color: #00A0C1;
}

/* ── Modern Pagination ── */
.pagination-modern {
   display: inline-flex;
   align-items: center;
   border: 1px solid #cbd5e1;
   border-radius: 8px;
   background-color: #fff;
   overflow: hidden;
   height: 34px;
}

.pagination-label {
   font-size: 0.8rem;
   color: #64748b;
   font-weight: 500;
   padding: 0 10px;
   white-space: nowrap;
}

.pagination-arrow {
   background-color: transparent;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 0.85rem;
   padding: 6px 10px;
   transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
   background-color: #f1f3f5;
}

.pagination-arrow:disabled {
   color: #cbd5e1;
   cursor: not-allowed;
}

/* ── Hidden Inline Selects for Popovers ── */
.table-select-priority,
.table-select-status {
   position: absolute;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   opacity: 0;
   cursor: pointer;
}

/* ── Page Header Styles ── */
.ms-page-header {
   margin-bottom: 20px;
   padding: 18px 22px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 16px rgba(0, 78, 90, 0.04);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.8px;
   margin-bottom: 4px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}

/* ── Modals & Backdrop ── */
.blur-backdrop {
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   backdrop-filter: blur(5px);
   background-color: rgba(0,0,0,0.4);
}

.modal-custom-width { max-width: 95vw !important; }
.modal-lead-form-width { max-width: 600px !important; width: 100% !important; }

.close-button-custom {
   position: absolute;
   top: 1rem;
   right: 1.5rem;
   font-size: 2rem;
   font-weight: bold;
   line-height: 1;
   color: #888;
   background: none;
   border: none;
   cursor: pointer;
   z-index: 10;
}
.close-button-custom:hover { color: #000; }

.lead-modal-overlay {
   position: fixed;
   top: 0;
   left: 0;
   width: 100vw;
   height: 100vh;
   background: rgba(15, 23, 42, 0.5);
   backdrop-filter: blur(4px);
   display: flex;
   justify-content: center;
   align-items: center;
   z-index: 1050;
}

.lead-modal {
   background: #fff;
   border-radius: 16px;
   width: 100%;
   max-width: 500px;
   box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
   overflow: hidden;
   animation: lead-modal-enter 0.2s ease-out;
}

@keyframes lead-modal-enter {
   from { opacity: 0; transform: translateY(10px) scale(0.98); }
   to { opacity: 1; transform: translateY(0) scale(1); }
}

.lead-modal__header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 18px 24px 0;
}

.lead-modal__title {
   font-weight: 700;
   font-size: 1.15rem;
   color: #1e293b;
   display: flex;
   align-items: center;
   gap: 8px;
   margin: 0;
}

.lead-modal__close {
   background: none;
   border: none;
   font-size: 1.6rem;
   color: #94a3b8;
   cursor: pointer;
   line-height: 1;
   padding: 0;
}

.lead-modal__close:hover { color: #ef4444; }

.lead-modal__divider {
   height: 1px;
   background: #e2e8f0;
   margin: 14px 24px 0;
}

.lead-modal__body {
   padding: 18px 24px 24px;
}

.lead-modal__actions {
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 20px;
   padding-top: 14px;
   border-top: 1px solid #f1f5f9;
}

.lead-form-group { margin-bottom: 16px; }
.lead-form-label {
   display: block;
   font-weight: 500;
   font-size: 0.875rem;
   color: #475569;
   margin-bottom: 6px;
}

.lead-required { color: #ef4444; }

.lead-form-input {
   width: 100%;
   padding: 10px 14px;
   border: 1.5px solid #e2e8f0;
   border-radius: 8px;
   font-size: 0.875rem;
   color: #334155;
   outline: none;
}

.lead-form-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
}

.lead-btn {
   padding: 8px 18px;
   border-radius: 8px;
   font-size: 0.875rem;
   font-weight: 600;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   border: none;
   transition: all 0.2s ease;
}

.lead-btn--primary {
   background: linear-gradient(135deg, #00A0C1, #33b5d1);
   color: #fff;
}

.lead-btn--primary:hover {
   background: linear-gradient(135deg, #008da8, #00A0C1);
   box-shadow: 0 4px 12px rgba(0, 160, 193, 0.3);
}

.lead-btn--ghost {
   background: transparent;
   color: #64748b;
   border: 1px solid #e2e8f0;
}

.lead-btn--ghost:hover {
   background: #f8fafc;
   color: #334155;
}

.lead-modal-fade-enter-active, .lead-modal-fade-leave-active { transition: opacity 0.2s ease; }
.lead-modal-fade-enter-from, .lead-modal-fade-leave-to { opacity: 0; }

.cursor-pointer { cursor: pointer; }
.not-allowed-cursor { cursor: not-allowed; }

.custom-switch-input {
   width: 2.25em !important;
   height: 1.15em !important;
   cursor: pointer;
}

.custom-switch-input:checked {
   background-color: #00A0C1 !important;
   border-color: #00A0C1 !important;
}

.custom-switch-input:focus {
   box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.25) !important;
   border-color: #00A0C1 !important;
}

/* ── Dropdown Item Checkbox (Vertex Blue) ── */
.dropdown-item-checkbox {
   transition: background-color 0.15s ease;
}

.dropdown-item-checkbox:hover {
   background-color: #f1f5f9;
   border-radius: 6px;
}

.dropdown-item-checkbox .form-check-input {
   accent-color: #00A0C1;
   cursor: pointer;
}

.dropdown-item-checkbox .form-check-input:checked {
   background-color: #00A0C1;
   border-color: #00A0C1;
}

.dropdown-item-checkbox .form-check-input:focus {
   border-color: #00A0C1;
   box-shadow: 0 0 0 0.25rem rgba(0, 160, 193, 0.25);
}

/* ── Modern Activity Modal Styling ── */
.modal-blur {
   backdrop-filter: blur(4px);
}

.modern-modal-content {
   border-radius: 16px;
   border: none;
   box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
   overflow: hidden;
   background: #ffffff;
}

.modern-activity-modal-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding: 18px 24px;
   background: #ffffff;
   border-bottom: 1px solid #e2e8f0;
}

.modal-header-icon-badge {
   width: 40px;
   height: 40px;
   border-radius: 10px;
   background: rgba(0, 160, 193, 0.1);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.1rem;
   color: #00A0C1;
   flex-shrink: 0;
}

.modal-header-icon-badge i {
   color: #00A0C1 !important;
}

.modern-activity-modal-header .modal-title {
   font-size: 1.05rem;
   color: #1e293b;
}

.modern-activity-modal-header .modal-subtitle {
   font-size: 0.8rem;
   color: #64748b;
}

.modern-modal-close-btn {
   width: 32px;
   height: 32px;
   border-radius: 8px;
   border: none;
   background: #f1f5f9;
   color: #64748b;
   display: flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   transition: all 0.15s ease;
}

.modern-modal-close-btn:hover {
   background: #e2e8f0;
   color: #1e293b;
}

.modern-activity-modal-body {
   background: #ffffff;
   max-height: 70vh;
   overflow-y: auto;
}

.modern-activity-modal-footer {
   padding: 12px 24px;
   background: #f8fafc;
   border-top: 1px solid #e2e8f0;
   display: flex;
   justify-content: flex-end;
}
</style>
